import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const defaultPerPageOptions = [10, 25, 50, 100];

const TablePagination = ({
	count,
	page,
	perPage = 10,
	goToAvail,
	onChangePage,
	onChangePerPage,
	onShowMore,
	perPageOptions = defaultPerPageOptions,
}) => {
	const [open, setOpen] = useState(false);

	const options = useMemo(() => {
		return [...perPageOptions, perPage]
			.filter(
				(item, index) => [...perPageOptions, perPage].indexOf(item) === index,
			)
			.sort(function (a, b) {
				return a - b;
			});
	}, [perPage, perPageOptions]);

	const handleClose = () => {
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const handleChangePagination = (event, value) => {
		if (onChangePage) {
			onChangePage(value - 1);
		}
	};

	const handleChange = (event) => {
		if (onChangePerPage) {
			onChangePerPage(Number(event.target.value));
		}
	};

	const handlePageChange = useCallback(
		(e) => {
			const page = e.target.value ? Number(e.target.value) - 1 : 0;
			if (onChangePage) {
				onChangePage(page);
			}
		},
		[onChangePage],
	);

	return (
		<Grid
			spacing={1}
			container
			alignItems="center"
			justifyContent="space-between"
			sx={{ width: 'auto' }}
		>
			<Grid item>
				<Stack direction="row" spacing={1} alignItems="center">
					{onShowMore && (
						<Box pr={1}>
							<Button variant="outlined" onClick={onShowMore}>
								<Typography variant="caption">
									<FormattedMessage
										id="TablePagination.showMore"
										defaultMessage="Показать еще"
									/>
								</Typography>
							</Button>
						</Box>
					)}

					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="caption" color="secondary">
							<FormattedMessage
								id="TablePagination.perPage"
								defaultMessage="Показывать по"
							/>
						</Typography>
						<FormControl sx={{ m: 1 }}>
							<Select
								id="demo-controlled-open-select"
								open={open}
								onClose={handleClose}
								onOpen={handleOpen}
								value={perPage}
								onChange={handleChange}
								size="small"
								sx={{ '& .MuiSelect-select': { py: 0.75, px: 1.25 } }}
							>
								{options.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>

					{goToAvail && (
						<>
							<Typography variant="caption" color="secondary">
								<FormattedMessage
									id="TablePagination.goTo"
									defaultMessage="Перейти на"
								/>
							</Typography>
							<TextField
								size="small"
								type="number"
								value={page}
								onChange={handlePageChange}
								sx={{
									'& .MuiOutlinedInput-input': {
										py: 0.75,
										px: 1.25,
										width: 36,
									},
								}}
							/>
						</>
					)}
				</Stack>
			</Grid>
			<Grid item sx={{ mt: { xs: 2, sm: 0 } }}>
				<Pagination
					sx={{ '& .MuiPaginationItem-root': { my: 0.5 } }}
					count={count}
					page={page}
					onChange={handleChangePagination}
					color="primary"
					variant="combined"
					showFirstButton
					showLastButton
				/>
			</Grid>
		</Grid>
	);
};

TablePagination.propTypes = {
	count: PropTypes.number,
	page: PropTypes.number,
	perPage: PropTypes.number,
	goToAvail: PropTypes.bool,
	perPageOptions: PropTypes.arrayOf(PropTypes.number),
	onShowMore: PropTypes.func,
	onChangePage: PropTypes.func,
	onChangePerPage: PropTypes.func,
};

export default TablePagination;
